import React, { useEffect, useState } from "react";
import { userService } from "../services/userService";
import GloablButton from "../components/GloablButton";
import featuredPlacementController from "../controllers/featuredPlacementController";
import productController from "../controllers/productController";
import promotionController from "../controllers/promotionController";
import vendorMenuController from "../controllers/vendorMenuController";
import AvailableCreditsIcon from "../icons/AvailableCreditsIcon";
import BoostPlanIcon from "../icons/BoostPlanIcon";
import CreditCostSummaryIcon from "../icons/CreditCostSummaryIcon";
import DominancePlanIcon from "../icons/DominancePlanIcon";
import LaunchPlanIcon from "../icons/LaunchPlanIcon";
import TrachRedIcon from "../icons/TrachRedIcon";
import VendorLayout from "./VendorLayout";
import { translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";


const tabs = ["Active Promotions", "Create Promotions", "Featured Placement"];

const tabLabelIds = {
  "Active Promotions": "btn_active_promotions",
  "Create Promotions": "btn_create_promotions",
  "Featured Placement": "txt_web_vendor_featured_placement",
};
const promotionStatusLabelIds = {
  Active: "txt_common_active", Schedule: "txt_web_vendor_promotion_scheduled_status",
};
const getPromotionTitle = (promotion) => promotion.hasSourceTitle
  ? promotion.title : translateLabel("txt_web_vendor_untitled_promotion", "Untitled promotion");

const PROMOTION_TYPES = [
  "BOGO (Buy One Get One)",
  "% off items",
  "Meal Deals / Family Deals",
  "Loyalty",
];

const promotionTypeLabelIds = {
  "BOGO (Buy One Get One)": "input_promotion_bogo",
  "% off items": "txt_common_off_items",
  "Meal Deals / Family Deals": "txt_web_vendor_meal_deals_family_deals",
  Loyalty: "txt_common_loyalty",
};
const displayPromotionType = (value) => promotionTypeLabelIds[value]
  ? translateLabel(promotionTypeLabelIds[value], value) : value;

const PROMOTION_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const EMPTY_PROMOTION_FORM = {
  discountAmount: "",
  endDate: "",
  item: "",
  minimumValue: "",
  numberOfOrders: "",
  promotionType: "",
  startDate: "",
};

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? response;

const isApiError = (response) => response?.isAxiosError || response?.response;

const wait = (durationMs) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });

const getPromotionCardKey = (promotion) => {
  if (promotion.productOfferId) return `offer:${promotion.productOfferId}`;
  if (promotion.campaignId) return `campaign:${promotion.campaignId}`;

  return `local:${promotion.title}:${promotion.duration}`;
};

const uniquePromotionCards = (items) => {
  const seen = new Set();

  return items.filter((item) => {
    const key = getPromotionCardKey(item);
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.products)) return payload.products;

  return [];
};

const toDisplayLabel = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const toPromotionStatus = (value) => {
  const status = String(value || "").toUpperCase();

  if (status.includes("SCHEDULE")) return "Schedule";
  if (status.includes("ACTIVE")) return "Active";

  return toDisplayLabel(value) || "Active";
};

const mapPromotion = (promotion, index) => {
  const startDate = promotion.startDate || "";
  const endDate = promotion.endDate || "";

  return {
    id:
      promotion.productOfferId ||
      promotion.promotionId ||
      promotion.campaignId ||
      `promotion-${index}`,
    campaignId: promotion.campaignId || promotion.campaignID || "",
    productOfferId:
      promotion.productOfferId ||
      promotion.promotionId ||
      promotion.offerId ||
      "",
    title: promotion.title || promotion.promotionName || "Untitled promotion",
    hasSourceTitle: Boolean(promotion.title || promotion.promotionName),
    description: promotion.description || "",
    type:
      toDisplayLabel(promotion.offerType) ||
      toDisplayLabel(promotion.categoryType) ||
      "--",
    discount:
      promotion.discountApplied ||
      [promotion.discountValue, toDisplayLabel(promotion.discountType)]
        .filter(Boolean)
        .join(" ") ||
      "--",
    duration: [startDate, endDate].filter(Boolean).join(" - ") || "--",
    status: toPromotionStatus(promotion.campaignsStatus),
  };
};

const formatPromotionApiDate = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const [year, month, day] = rawValue.split("-").map(Number);
  if (!year || !month || !day) return rawValue;

  return `${PROMOTION_MONTHS[month - 1]} ${day},${year}`;
};

const getPromotionDiscountNumber = (promotion) => {
  const rawValue =
    promotion.discount ||
    promotion.discountApplied ||
    promotion.discountValue ||
    "";
  const match = String(rawValue).match(/-?\d+(\.\d+)?/);

  return match ? Number(match[0]) : NaN;
};

const parseCreditAmount = (value) => {
  const match = String(value || "").match(/-?\d+(\.\d+)?/);

  return match ? Number(match[0]) : 0;
};

const getDaysBetweenDates = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const diffMs = end.getTime() - start.getTime();

  return Math.max(1, Math.round(diffMs / 86400000));
};

const uniqueStringList = (values) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const FEATURED_CATEGORY_TYPES = [
  "APPETIZERS",
  "MAIN_COURSES",
  "DESSERTS",
  "SOFT_DRINKS",
];

const normalizeFeaturedCategoryType = (value) => {
  const rawValue = Array.isArray(value) ? value[0] : value;
  const normalized = String(rawValue || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");

  if (FEATURED_CATEGORY_TYPES.includes(normalized)) return normalized;
  if (normalized.includes("APPETIZER") || normalized.includes("STARTER")) {
    return "APPETIZERS";
  }
  if (
    normalized.includes("DESSERT") ||
    normalized.includes("SWEET") ||
    normalized.includes("CAKE")
  ) {
    return "DESSERTS";
  }
  if (
    normalized.includes("DRINK") ||
    normalized.includes("BEVERAGE") ||
    normalized.includes("SODA") ||
    normalized.includes("JUICE")
  ) {
    return "SOFT_DRINKS";
  }

  return "MAIN_COURSES";
};

const mapFeaturedRows = (payload) => {
  const rows = Array.isArray(payload) ? payload : [];

  return rows.flatMap((row, rowIndex) => {
    const products = Array.isArray(row.products) ? row.products : [];

    return products.map((product, productIndex) => ({
      id:
        row.featuredId ||
        row.productOfferId ||
        `${rowIndex}-${productIndex}`,
      availableCredits: parseCreditAmount(row.availableCredits),
      categoryType: product.categoryType || [],
      creditsUsed: product.creditsUsed || "",
      endDate: product.endDate || "",
      productName: Array.isArray(product.productName)
        ? product.productName.join(", ")
        : product.productName || "Featured product",
      startDate: product.startDate || "",
      status: product.status || row.status || "ACTIVE",
    }));
  });
};

const findCreatedPromotion = (items, criteria) => {
  const targetTitle = criteria.campaignName.trim().toLowerCase();
  const targetDuration = `${criteria.startDate} - ${criteria.endDate}`;

  return (
    items.find((item) => {
      const title = String(item.title || "").trim().toLowerCase();
      const sameTitle =
        title &&
        targetTitle &&
        (title === targetTitle ||
          title.includes(targetTitle) ||
          targetTitle.includes(title));
      const sameDuration = item.duration === targetDuration;
      const sameDiscount =
        getPromotionDiscountNumber(item) === criteria.discountValue;

      return sameTitle || (sameDuration && sameDiscount);
    }) || null
  );
};

const mapPromotionCatalogItem = (item, index) => {
  const productId = item.productID || item.productId || item.id || "";
  const skuId = item.skuID || item.skuId || "";
  const category =
    item.categoryName ||
    toDisplayLabel(item.categoryType || item.category) ||
    "";

  return {
    id: [productId || "product", skuId || index].join("-"),
    name: item.productName || item.name || item.title || "Unnamed item",
    category,
    categoryId: item.categoryId || item.categoryID || "",
    categoryType: item.categoryType || item.category || "",
    productId,
    productOfferId: item.productOfferId || item.productOfferID || "",
    skuId,
  };
};

const FEATURED_PLANS = [
  {
    id: "launch",
    Icon: LaunchPlanIcon,
    name: "Launch Plan",
    description:
      "Perfect for new vendors or small businesses experimenting with promotions and boosted placement.",
    price: "$49",
    duration: "30 days",
    credits: "500",
    badge: "Most Popular",
    features: [
      "500 credits",
      "30-day validity",
      "Basic promotions",
      "Email support",
      "Usage analytics",
    ],
  },
  {
    id: "boost",
    Icon: BoostPlanIcon,
    name: "Boost Plan",
    description:
      "Designed for active vendors who want consistent visibility and use promotions regularly.",
    price: "$129",
    duration: "60 days",
    credits: "1500",
    highlight: true,
    save: "Save 15%",
    features: [
      "1500 credits (15% bonus)",
      "60-day validity",
      "All promotion types",
      "Priority support",
      "Advanced analytics",
      "Custom scheduling",
    ],
  },
  {
    id: "dominance",
    Icon: DominancePlanIcon,
    name: "Dominance Plan",
    description:
      "Built for high-volume stores that need maximum visibility and broader marketing support.",
    price: "$299",
    duration: "90 days",
    credits: "4000",
    save: "Save 25%",
    features: [
      "4000 credits (25% bonus)",
      "90-day validity",
      "Unlimited promotions",
      "Dedicated account manager",
      "Premium analytics",
      "API access",
      "Custom integrations",
    ],
  },
];

function VendorPromotions() {
  useTranslationSnapshot();
  const [activeTab, setActiveTab] = useState("Active Promotions");
  const [promotions, setPromotions] = useState([]);
  const [availableCredits, setAvailableCredits] = useState(null);
  const [isPromotionsLoading, setIsPromotionsLoading] = useState(true);
  const [isCreatingPromotion, setIsCreatingPromotion] = useState(false);
  const [deletingPromotionId, setDeletingPromotionId] = useState("");
  const [isPromotionTypeOpen, setIsPromotionTypeOpen] = useState(false);
  const [isAddItemsModalOpen, setIsAddItemsModalOpen] = useState(false);
  const [promotionCatalogItems, setPromotionCatalogItems] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [isFeaturedLoading, setIsFeaturedLoading] = useState(false);
  const [isActivatingFeatured, setIsActivatingFeatured] = useState(false);
  const [deletingFeaturedId, setDeletingFeaturedId] = useState("");
  const [isPromotionItemsLoading, setIsPromotionItemsLoading] = useState(false);
  const [hasLoadedPromotionItems, setHasLoadedPromotionItems] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [featuredEnabled, setFeaturedEnabled] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [form, setForm] = useState(EMPTY_PROMOTION_FORM);
  const [featuredStartDate, setFeaturedStartDate] = useState("2026-04-25");
  const [featuredEndDate, setFeaturedEndDate] = useState("2026-05-01");
  const featuredDurationDays = getDaysBetweenDates(
    featuredStartDate,
    featuredEndDate,
  );
  const featuredCostPerDay = 20;
  const featuredTotalCost = featuredDurationDays * featuredCostPerDay;
  const featuredRemainingCredits =
    availableCredits === null
      ? 0
      : Math.max(0, Number(availableCredits) - featuredTotalCost);

  useEffect(() => {
    let isMounted = true;

    const fetchPromotions = async () => {
      setIsPromotionsLoading(true);

      try {

        if (!userService.getJwt()) {
          if (!isMounted) return;
          setPromotions([]);
          setAvailableCredits(null);
          return;
        }

        const response = await promotionController.getPromotions({
          Authorization: userService.getJwt(),
        });

        if (!isMounted) return;

        if (isApiError(response)) {
          setPromotions([]);
          setAvailableCredits(null);
          return;
        }

        const payload = unwrapPayload(response);
        const rows = Array.isArray(payload?.activeAndScheduledCampaignsDTO)
          ? payload.activeAndScheduledCampaignsDTO
          : [];
        const mappedPromotions = rows.map(mapPromotion);

        setPromotions(uniquePromotionCards(mappedPromotions));
        setAvailableCredits(payload?.availableCredits ?? null);
      } catch {
        if (!isMounted) return;
        setPromotions([]);
        setAvailableCredits(null);
      } finally {
        if (isMounted) {
          setIsPromotionsLoading(false);
        }
      }
    };

    fetchPromotions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "Featured Placement") return;

    if (!hasLoadedPromotionItems) {
      loadPromotionItems();
    }
    loadFeaturedCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const updateField = (field) => (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: event.target.value,
    }));
  };

  const promotionItemCategories = Array.from(
    new Set(promotionCatalogItems.map((item) => item.category)),
  );

  const visiblePromotionItems = selectedCategory
    ? promotionCatalogItems.filter((item) => item.category === selectedCategory)
    : promotionCatalogItems;

  const updateFeaturedStartDate = (event) => {
    const nextStartDate = event.target.value;

    setFeaturedStartDate(nextStartDate);
    if (featuredEndDate && nextStartDate && featuredEndDate < nextStartDate) {
      setFeaturedEndDate(nextStartDate);
    }
  };

  const updateFeaturedEndDate = (event) => {
    setFeaturedEndDate(event.target.value);
  };

  const loadPromotionItems = async () => {
    setIsPromotionItemsLoading(true);

    try {
            const companyID = userService.getUserData().Compid;

      if (!userService.getJwt()) {
        setPromotionCatalogItems([]);
        setHasLoadedPromotionItems(true);
        return;
      }

      const response = await productController.getProductCatalog({
        Authorization: userService.getJwt(),
        companyID,
        page: 1,
        sizePage: 100,
      });

      if (isApiError(response)) {
        setPromotionCatalogItems([]);
        setHasLoadedPromotionItems(true);
        return;
      }

      const nextItems = getRows(unwrapPayload(response)).map(
        mapPromotionCatalogItem,
      );

      setPromotionCatalogItems(nextItems);
      setSelectedCategory((currentCategory) =>
        nextItems.some((item) => item.category === currentCategory)
          ? currentCategory
          : nextItems[0]?.category || "",
      );
      setHasLoadedPromotionItems(true);
    } catch {
      setPromotionCatalogItems([]);
      setHasLoadedPromotionItems(true);
    } finally {
      setIsPromotionItemsLoading(false);
    }
  };

  const openAddItemsModal = () => {
    setIsAddItemsModalOpen(true);

    if (!hasLoadedPromotionItems) {
      loadPromotionItems();
    }
  };

  const loadFeaturedCategories = async () => {
    setIsFeaturedLoading(true);

    try {
            const response = await featuredPlacementController.getFeaturedCategories({
        Authorization: userService.getJwt(),
      });

      if (isApiError(response)) {
        setFeaturedItems([]);
        return;
      }

      const payload = unwrapPayload(response);
      const nextFeaturedItems = mapFeaturedRows(payload);
      const creditsFromFeatured = Array.isArray(payload)
        ? payload.find((item) => item?.availableCredits)?.availableCredits
        : "";

      setFeaturedItems(nextFeaturedItems);
      if (creditsFromFeatured) {
        setAvailableCredits(parseCreditAmount(creditsFromFeatured));
      }
      setFeaturedEnabled(nextFeaturedItems.length > 0);
    } finally {
      setIsFeaturedLoading(false);
    }
  };

  const resolveFeaturedCategoryId = async (categoryTypes) => {
    const firstSelectedCategoryId = promotionCatalogItems.find(
      (item) =>
        selectedItems.includes(item.id) && item.categoryId,
    )?.categoryId;

    if (firstSelectedCategoryId) return firstSelectedCategoryId;

        const categoriesResponse = await vendorMenuController.getMenuCategories({
      Authorization: userService.getJwt(),
      companyId: userService.getUserData().Compid,
      includeItems: false,
    });
    if (isApiError(categoriesResponse)) return "";

    const normalizedTypes = new Set(
      categoryTypes.map(normalizeFeaturedCategoryType),
    );
    const categoryRows = getRows(unwrapPayload(categoriesResponse));
    const matchedCategory = categoryRows.find((category) => {
      const categoryValues = [
        category.categoryType,
        category.categoryName,
        category.name,
      ].map(normalizeFeaturedCategoryType);

      return categoryValues.some((value) => normalizedTypes.has(value));
    });

    return matchedCategory?.categoryId || matchedCategory?.id || "";
  };

  const activateFeaturedPlacement = async () => {
    if (isActivatingFeatured) return;

    const selectedFeaturedItems = selectedItems.length
      ? promotionCatalogItems.filter((item) => selectedItems.includes(item.id))
      : promotionCatalogItems.slice(0, 1);

    if (!selectedFeaturedItems.length) return;

    setIsActivatingFeatured(true);

    try {
            const categoryTypes = uniqueStringList(
        selectedFeaturedItems.map((item) =>
          normalizeFeaturedCategoryType(item.categoryType || item.category),
        ),
      );
      const categoryId =
        selectedFeaturedItems.find((item) => item.categoryId)?.categoryId ||
        (await resolveFeaturedCategoryId(categoryTypes));
      const request = {
        categoryId,
        categoryType: categoryTypes,
        creditCostSummary: {
          creditsPerProduct: `${Math.ceil(
            featuredTotalCost / selectedFeaturedItems.length,
          )} credits`,
          productsSelected: `${selectedFeaturedItems.length} product${
            selectedFeaturedItems.length === 1 ? "" : "s"
          }`,
          promotionDuration: `${featuredDurationDays} days`,
          remainingAfterActivation: `${featuredRemainingCredits} credits`,
          totalCreditsRequired: `${featuredTotalCost} credits`,
        },
        endDate: formatPromotionApiDate(featuredEndDate),
        productAndSku: {
          productID: uniqueStringList(
            selectedFeaturedItems.map((item) => item.productId),
          ),
          skuId: uniqueStringList(selectedFeaturedItems.map((item) => item.skuId)),
        },
        startDate: formatPromotionApiDate(featuredStartDate),
      };

      if (
        !request.categoryId ||
        !request.categoryType.length ||
        !request.startDate ||
        !request.endDate ||
        !request.productAndSku.productID.length ||
        !request.productAndSku.skuId.length
      ) {
        return;
      }

      const response = await featuredPlacementController.addFeatured({
        Authorization: userService.getJwt(),
        request,
      });

      if (isApiError(response)) return;

      await loadFeaturedCategories();
    } finally {
      setIsActivatingFeatured(false);
    }
  };

  const deleteFeatured = async (featuredId) => {
    if (!featuredId || deletingFeaturedId) return;

    setDeletingFeaturedId(featuredId);

    try {
            const response = await featuredPlacementController.deleteFeaturedById({
        Authorization: userService.getJwt(),
        featuredId,
      });

      if (isApiError(response)) return;

      setFeaturedItems((currentItems) =>
        currentItems.filter((item) => item.id !== featuredId),
      );
    } finally {
      setDeletingFeaturedId("");
    }
  };

  const removePromotion = async (promotion) => {
    const promotionId = promotion.id;
    const productOfferId = promotion.productOfferId;

    if (!promotionId || deletingPromotionId) return;

    setDeletingPromotionId(promotionId);

    try {

      if (!productOfferId) {
        return;
      }

      const response =
        await promotionController.deletePromotionByProductOfferId({
          Authorization: userService.getJwt(),
          productOfferId,
        });

      if (isApiError(response)) return;

      setPromotions((currentPromotions) =>
        currentPromotions.filter((item) => item.id !== promotionId),
      );
    } finally {
      setDeletingPromotionId("");
    }
  };

  const toggleSelectedItem = (itemName) => {
    setSelectedItems((currentItems) =>
      currentItems.includes(itemName)
        ? currentItems.filter((item) => item !== itemName)
        : [...currentItems, itemName],
    );
  };

  const addSelectedItems = () => {
    const selectedItemNames = promotionCatalogItems
      .filter((item) => selectedItems.includes(item.id))
      .map((item) => item.name);

    setForm((currentForm) => ({
      ...currentForm,
      item: selectedItemNames.join(", "),
    }));
    setIsAddItemsModalOpen(false);
  };

  const canCreatePromotion = Boolean(
    form.discountAmount &&
      form.endDate &&
      form.item &&
      form.numberOfOrders &&
      form.promotionType &&
      form.startDate,
  );

  const createPromotion = async (event) => {
    event.preventDefault();
    if (!canCreatePromotion || isCreatingPromotion) return;

    setIsCreatingPromotion(true);

    try {
            const selectedPromotionItems = promotionCatalogItems.filter((item) =>
        selectedItems.includes(item.id),
      );
      const campaignItemNames = selectedPromotionItems
        .map((item) => item.name)
        .join(", ");
      const basePromotionType = form.promotionType.trim();
      const numericDiscount = Number(form.discountAmount);
      const promotionStartDate = formatPromotionApiDate(form.startDate);
      const promotionEndDate = formatPromotionApiDate(form.endDate);
      const campaignName =
        [
          basePromotionType,
          campaignItemNames,
          form.startDate && form.endDate
            ? `${form.startDate} to ${form.endDate}`
            : "",
          form.discountAmount ? `${form.discountAmount}%` : "",
        ]
          .filter(Boolean)
          .join(" - ") || `Promotion ${Date.now()}`;
      const description = "Promotion campaign";

      const createResponse = await promotionController.addCampaign({
        Authorization: userService.getJwt(),
        request: {
          campaignName,
          description,
        },
      });

      if (isApiError(createResponse)) return;

      let mappedPromotions = [];
      let matchedPromotion = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt > 0) {
          await wait(700);
        }

        const listResponse = await promotionController.getPromotions({
          Authorization: userService.getJwt(),
        });

        if (isApiError(listResponse)) continue;

        const payload = unwrapPayload(listResponse);
        const rows = Array.isArray(payload?.activeAndScheduledCampaignsDTO)
          ? payload.activeAndScheduledCampaignsDTO
          : [];

        mappedPromotions = rows.map(mapPromotion);
        setAvailableCredits(payload?.availableCredits ?? null);
        matchedPromotion = findCreatedPromotion(mappedPromotions, {
          campaignName,
          discountValue: numericDiscount,
          endDate: promotionEndDate,
          startDate: promotionStartDate,
        });

        if (matchedPromotion) break;
      }

      setPromotions(uniquePromotionCards(mappedPromotions));
      setForm(EMPTY_PROMOTION_FORM);
      setSelectedItems([]);
      setActiveTab("Active Promotions");
    } finally {
      setIsCreatingPromotion(false);
    }
  };

  return (
    <VendorLayout activeKey="promotions">
      <section className="vendor-promotions-page">
        <div className="vendor-promotions-header">
          <div className="vendor-menu-heading">
            <div>
              <div className="vendor-promotion-page-title">
                {translateLabel("txt_web_vendor_promotions_marketing", "Promotions & Marketing")}
              </div>
              <div className="vendor-promotion-page-subtitle">
                {translateLabel("txt_web_vendor_drive_sales_visibility", "Drive sales and increase visibility")}
              </div>
            </div>
          </div>

          <div className="vendor-store-online">
            <div aria-hidden="true" /> {translateLabel("txt_store_online", "Store Online")} </div>
        </div>

        <div className="vendor-promotion-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              className={activeTab === tab ? "active" : ""}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              key={tab}
              onClick={() => setActiveTab(tab)}
            >
              {translateLabel(tabLabelIds[tab], tab)}
            </button>
          ))}
        </div>

        {activeTab === "Active Promotions" ? (
          <>
            <section className="vendor-promotion-credits">
              <div className="vendor-promotion-credits-icon">
                <AvailableCreditsIcon />
              </div>
              <div className="vendor-promotion-credit-count">
                {availableCredits ?? "--"}
              </div>
              <div className="vendor-promotion-credit-label">
                {translateLabel("txt_web_vendor_available_credits", "Available Credits")}
              </div>
              <div className="vendor-promotion-credit-actions">
                <GloablButton
                  className="vendor-promotion-credit-button"
                  width="86px"
                  height="28px"
                  background="#ffffff"
                  border="1px solid #d0d5dd"
                  borderRadius="5px"
                  color="#344054"
                  fontSize="9px"
                  lineHeight="13px"
                  padding="0 14px"
                  text={translateLabel("lnk_view_history", "View History")}
                />
                <GloablButton
                  className="vendor-promotion-credit-button"
                  width="86px"
                  height="28px"
                  background="#fa9c03"
                  borderRadius="5px"
                  fontSize="9px"
                  lineHeight="13px"
                  padding="0 14px"
                  text={translateLabel("btn_buy_credits", "Buy Credits")}
                />
              </div>
            </section>

            <div className="vendor-promotion-list">
              {promotions.map((promotion) => (
                <article className="vendor-promotion-card" key={promotion.id}>
                  <div className="vendor-promotion-copy">
                    <div className="vendor-promotion-card-title">
                      {getPromotionTitle(promotion)}
                    </div>
                    <div className="vendor-promotion-card-description">
                      {promotion.description}
                    </div>
                    <div className="vendor-promotion-details">
                      <div>
                        <div className="vendor-promotion-detail-label">
                          {translateLabel("txt_web_vendor_promotion_type", "Promotion Type")}
                        </div>
                        <div className="vendor-promotion-detail-value">
                          {promotion.type}
                        </div>
                      </div>
                      <div>
                        <div className="vendor-promotion-detail-label"> {translateLabel("input_promotion_discount", "Discount")} </div>
                        <div className="vendor-promotion-detail-value">
                          {promotion.discount}
                        </div>
                      </div>
                      <div>
                        <div className="vendor-promotion-detail-label"> {translateLabel("txt_business_duration", "Duration")} </div>
                        <div className="vendor-promotion-detail-value">
                          {promotion.duration}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="vendor-promotion-actions">
                    <div
                      className={`vendor-promotion-status vendor-promotion-status-${promotion.status.toLowerCase()}`}
                    >
                      {promotionStatusLabelIds[promotion.status]
                        ? translateLabel(promotionStatusLabelIds[promotion.status], promotion.status)
                        : promotion.status}
                    </div>
                    <button
                      type="button"
                      aria-label={translateLabel("txt_web_vendor_remove_value1", "Remove {value1}", { value1: getPromotionTitle(promotion) })}
                      disabled={deletingPromotionId === promotion.id}
                      onClick={() => removePromotion(promotion)}
                    >
                      <TrachRedIcon />
                    </button>
                  </div>
                </article>
              ))}

              {isPromotionsLoading && (
                <div className="vendor-promotion-empty">
                  {translateLabel("txt_web_vendor_loading_promotions", "Loading promotions...")}
                </div>
              )}

              {!isPromotionsLoading && promotions.length === 0 && (
                <div className="vendor-promotion-empty">
                  {translateLabel("txt_web_vendor_no_active_promotions", "No active promotions found.")}
                </div>
              )}
            </div>
          </>
        ) : activeTab === "Create Promotions" ? (
          <form
            className="vendor-promotion-form"
            onSubmit={createPromotion}
          >
            <div className="vendor-promotion-form-heading">
              <div className="vendor-promotion-form-title">
                {translateLabel("txt_web_vendor_promotion_details", "Promotion Details")}
              </div>
              <div className="vendor-promotion-form-subtitle">
                {translateLabel("txt_web_vendor_configure_promotion", "Configure your promotion settings and targeting")}
              </div>
            </div>

            <div className="vendor-promotion-form-grid">
              <label className="vendor-promotion-field vendor-promotion-field-full">
                <div className="vendor-promotion-field-label">
                  {translateLabel("txt_web_vendor_promotion_type", "Promotion Type")}<div>*</div>
                </div>
                <div
                  className="vendor-promotion-type-select"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setIsPromotionTypeOpen(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isPromotionTypeOpen}
                    onClick={() =>
                      setIsPromotionTypeOpen((current) => !current)
                    }
                  >
                    <div className="vendor-promotion-type-value">
                      {displayPromotionType(form.promotionType) || translateLabel("input_promotion_type_placeholder", "Select promotion type")}
                    </div>
                    <div
                      className="vendor-promotion-chevron"
                      aria-hidden="true"
                    >
                      ˅
                    </div>
                  </button>

                  {isPromotionTypeOpen && (
                    <div
                      className="vendor-promotion-type-menu"
                      role="listbox"
                    >
                      {PROMOTION_TYPES.map((promotionType) => (
                        <button
                          type="button"
                          role="option"
                          aria-selected={form.promotionType === promotionType}
                          key={promotionType}
                          onClick={() => {
                            setForm((currentForm) => ({
                              ...currentForm,
                              promotionType,
                            }));
                            setIsPromotionTypeOpen(false);
                          }}
                        >
                          {displayPromotionType(promotionType)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </label>

              <label className="vendor-promotion-field vendor-promotion-field-full">
                <div className="vendor-promotion-field-label"> {translateLabel("txt_web_vendor_add_item", "Add Item")}<div>*</div>
                </div>
                <div className="vendor-promotion-add-item">
                  <input
                    type="text"
                    value={form.item}
                    placeholder={translateLabel("btn_add_promotion_items", "Add your items")}
                    onChange={updateField("item")}
                  />
                  <button
                    type="button"
                    aria-label={translateLabel("txt_web_vendor_add_item", "Add item")}
                    onClick={openAddItemsModal}
                  >
                    <div aria-hidden="true">+</div>
                  </button>
                </div>
              </label>

              <label className="vendor-promotion-field">
                <div className="vendor-promotion-field-label">
                  {translateLabel("txt_web_vendor_promotion_start_date", "Start Date")}<div>*</div>
                </div>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={updateField("startDate")}
                />
              </label>

              <label className="vendor-promotion-field">
                <div className="vendor-promotion-field-label">
                  {translateLabel("txt_web_vendor_promotion_end_date", "End Date")}<div>*</div>
                </div>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={updateField("endDate")}
                />
              </label>

              <label className="vendor-promotion-field">
                <div className="vendor-promotion-field-label">
                  {translateLabel("txt_web_vendor_discount_amount_percent", "Discount Amount - (%)")}<div>*</div>
                </div>
                <input
                  type="number"
                  min="0"
                  value={form.discountAmount}
                  placeholder={translateLabel("txt_web_vendor_example_twenty", "e.g., 20")}
                  onChange={updateField("discountAmount")}
                />
              </label>

              <label className="vendor-promotion-field">
                <div className="vendor-promotion-field-label">
                  {translateLabel("txt_web_vendor_minimum_value", "Minimum Value")}
                  <div className="vendor-promotion-help-icon" aria-hidden="true">
                    ?
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  value={form.minimumValue}
                  placeholder={translateLabel("txt_web_vendor_example_twenty", "e.g., 20")}
                  onChange={updateField("minimumValue")}
                />
              </label>

              <label className="vendor-promotion-field vendor-promotion-field-full">
                <div className="vendor-promotion-field-label">
                  {translateLabel("txt_web_vendor_number_of_order", "Number of order")}<div>*</div>
                </div>
                <input
                  type="number"
                  min="1"
                  value={form.numberOfOrders}
                  placeholder={translateLabel("txt_web_vendor_example_twenty", "e.g., 20")}
                  onChange={updateField("numberOfOrders")}
                />
              </label>
            </div>

            <div className="vendor-promotion-form-actions">
              <GloablButton
                className="vendor-promotion-form-button"
                width="72px"
                height="30px"
                background="#ffffff"
                border="1px solid #d0d5dd"
                borderRadius="7px"
                color="#344054"
                fontSize="10px"
                lineHeight="14px"
                padding="0 12px"
                text={translateLabel("btn_cancel", "Cancel")}
                onClick={() => setForm(EMPTY_PROMOTION_FORM)}
              />
              <GloablButton
                className="vendor-promotion-form-button"
                type="submit"
                width="152px"
                height="30px"
                background="#fa9c03"
                borderRadius="7px"
                fontSize="10px"
                lineHeight="14px"
                padding="0 12px"
                text={
                  isCreatingPromotion
                    ? translateLabel("txt_web_vendor_creating", "Creating...")
                    : translateLabel("txt_web_vendor_create_promotion_0_credits", "Create Promotion (0 credits)")
                }
                disabled={!canCreatePromotion || isCreatingPromotion}
              />
            </div>

            {isAddItemsModalOpen && (
              <div
                className="vendor-promotion-modal-backdrop"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    setIsAddItemsModalOpen(false);
                  }
                }}
              >
                <div
                  className="vendor-promotion-add-items-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="vendor-promotion-add-items-title"
                >
                  <div className="vendor-promotion-add-items-header">
                    <div>
                      <div
                        className="vendor-promotion-add-items-title"
                        id="vendor-promotion-add-items-title"
                      >
                        {translateLabel("txt_web_vendor_add_items", "Add Items")}
                      </div>
                      <div className="vendor-promotion-add-items-subtitle">
                        {translateLabel("txt_web_vendor_choose_category_items", "Select a category and choose items to add")}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={translateLabel("txt_web_vendor_close_add_items", "Close Add Items")}
                      onClick={() => setIsAddItemsModalOpen(false)}
                    >
                      <div aria-hidden="true">×</div>
                    </button>
                  </div>

                  <label className="vendor-promotion-modal-field">
                    <div className="vendor-promotion-modal-label"> {translateLabel("btn_category", "Category")} </div>
                    <div className="vendor-promotion-modal-select">
                      <select
                        value={selectedCategory}
                        onChange={(event) => {
                          setSelectedCategory(event.target.value);
                          setSelectedItems([]);
                        }}
                      >
                        {promotionItemCategories.map((category) => (
                          <option value={category} key={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                      <div
                        className="vendor-promotion-chevron"
                        aria-hidden="true"
                      >
                        ˅
                      </div>
                    </div>
                  </label>

                  <div className="vendor-promotion-modal-field">
                    <div className="vendor-promotion-modal-label">{translateLabel("txt_web_vendor_items", "Items")}</div>
                    <div className="vendor-promotion-item-options">
                      {isPromotionItemsLoading && (
                        <div className="vendor-promotion-empty">
                          {translateLabel("txt_web_vendor_loading_promotion_items", "Loading items...")}
                        </div>
                      )}

                      {!isPromotionItemsLoading &&
                        visiblePromotionItems.map((item) => (
                        <label key={item.id}>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => toggleSelectedItem(item.id)}
                          />
                          <div>{item.name}</div>
                        </label>
                      ))}

                      {!isPromotionItemsLoading &&
                        visiblePromotionItems.length === 0 && (
                          <div className="vendor-promotion-empty">
                            {translateLabel("txt_web_vendor_no_promotion_items", "No items found.")}
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="vendor-promotion-modal-actions">
                    <GloablButton
                      className="vendor-promotion-modal-button"
                      width="70px"
                      height="30px"
                      background="#ffffff"
                      border="1px solid #d0d5dd"
                      borderRadius="5px"
                      color="#344054"
                      fontSize="10px"
                      lineHeight="14px"
                      padding="0 12px"
                      text={translateLabel("btn_cancel", "Cancel")}
                      onClick={() => setIsAddItemsModalOpen(false)}
                    />
                    <GloablButton
                      className="vendor-promotion-modal-button"
                      width="118px"
                      height="30px"
                      background="#fa9c03"
                      borderRadius="5px"
                      fontSize="10px"
                      lineHeight="14px"
                      padding="0 12px"
                      text={translateLabel("btn_add_selected_items", "Add Selected Items")}
                      disabled={selectedItems.length === 0}
                      onClick={addSelectedItems}
                    />
                  </div>
                </div>
              </div>
            )}
          </form>
        ) : (
          <div className="vendor-featured-placement">
            <section className="vendor-featured-card">
              <div className="vendor-featured-card-header">
                <div>
                  <div className="vendor-featured-card-title">{translateLabel("txt_web_vendor_featured_heading", "Featured")}</div>
                  <div className="vendor-featured-card-subtitle">
                    {translateLabel("txt_web_vendor_featured_store_description", "Promote your store on the homepage featured section.")}
                  </div>
                </div>
                <div
                  className={`vendor-featured-toggle ${
                    featuredEnabled ? "active" : ""
                  }`}
                  role="switch"
                  aria-label={translateLabel("txt_web_vendor_featured_heading", "Featured")}
                  aria-checked={featuredEnabled}
                  tabIndex={0}
                  onClick={() => setFeaturedEnabled((current) => !current)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setFeaturedEnabled((current) => !current);
                    }
                  }}
                >
                  <div />
                </div>
              </div>

              <div className="vendor-featured-period">
                <div className="vendor-featured-period-title">
                  {translateLabel("txt_web_vendor_promotion_period", "Promotion Period")}
                </div>
                <div className="vendor-featured-date-row">
                  <input
                    type="date"
                    aria-label={translateLabel("txt_web_vendor_promotion_start_date", "Start Date")}
                    value={featuredStartDate}
                    onChange={updateFeaturedStartDate}
                  />
                  <div>{translateLabel("txt_time_to", "to")}</div>
                  <input
                    min={featuredStartDate}
                    type="date"
                    aria-label={translateLabel("txt_web_vendor_promotion_end_date", "End Date")}
                    value={featuredEndDate}
                    onChange={updateFeaturedEndDate}
                  />
                </div>
                <div className="vendor-featured-period-note">
                  {translateLabel("txt_web_vendor_featured_cost_note", "The system will automatically calculate the credit cost based on the selection duration.")}
                </div>
              </div>

              <div className="vendor-featured-summary">
                <div className="vendor-featured-summary-title">
                  <CreditCostSummaryIcon />
                  {translateLabel("txt_web_vendor_credit_cost_summary", "Credit Cost Summary")}
                </div>
                <div>
                  <div>{translateLabel("txt_your_available_credits", "Your available credits")}</div>
                  <div className="vendor-featured-summary-value">
                    {availableCredits ?? "--"} {translateLabel("txt_business_credits", "credits")} </div>
                </div>
                <div>
                  <div>{translateLabel("txt_promotion_duration", "Promotion duration")}</div>
                  <div className="vendor-featured-summary-value">
                    {translateLabel("txt_web_vendor_duration_days_count", "{count} days", { count: featuredDurationDays })}
                  </div>
                </div>
                <div>
                  <div>{translateLabel("txt_web_vendor_cost_per_day", "Cost per day")}</div>
                  <div className="vendor-featured-summary-value">
                    {featuredCostPerDay} {translateLabel("txt_business_credits", "credits")} </div>
                </div>
                <div>
                  <div>{translateLabel("txt_web_vendor_total_promotion_cost", "Total promotion cost")}</div>
                  <div className="vendor-featured-summary-value danger">
                    {featuredTotalCost} {translateLabel("txt_business_credits", "credits")} </div>
                </div>
                <div>
                  <div>{translateLabel("txt_web_vendor_remaining_after_activation", "Remaining after activation")}</div>
                  <div className="vendor-featured-summary-value">
                    {availableCredits === null
                      ? "--"
                      : featuredRemainingCredits}{" "} {translateLabel("txt_business_credits", "credits")} </div>
                </div>
              </div>

              <div className="vendor-featured-current-list">
                {isFeaturedLoading && (
                  <div className="vendor-promotion-empty">
                    {translateLabel("txt_web_vendor_loading_featured_placements", "Loading featured placements...")}
                  </div>
                )}

                {!isFeaturedLoading &&
                  featuredItems.map((item) => (
                    <div className="vendor-featured-current-item" key={item.id}>
                      <div>
                        <div>{item.productName}</div>
                        <span>
                          {item.startDate} - {item.endDate}
                        </span>
                      </div>
                      <button
                        type="button"
                        aria-label={translateLabel("txt_web_vendor_delete_featured_value1", "Delete featured {value1}", { value1: item.productName })}
                        disabled={deletingFeaturedId === item.id}
                        onClick={() => deleteFeatured(item.id)}
                      >
                        <TrachRedIcon />
                      </button>
                    </div>
                  ))}
              </div>

              <div className="vendor-featured-actions">
                <GloablButton
                  className="vendor-featured-action-button"
                  width="100%"
                  height="30px"
                  background="#fa9c03"
                  borderRadius="6px"
                  fontSize="10px"
                  lineHeight="14px"
                  padding="0 12px"
                  text={
                    isActivatingFeatured
                      ? translateLabel("txt_web_vendor_activating", "Activating...")
                      : translateLabel("btn_activate_featured_placement", "Activate Featured Placement")
                  }
                  disabled={
                    isActivatingFeatured || !promotionCatalogItems.length
                  }
                  onClick={activateFeaturedPlacement}
                />
                <GloablButton
                  className="vendor-featured-action-button"
                  width="100%"
                  height="30px"
                  background="#fa9c03"
                  borderRadius="6px"
                  fontSize="10px"
                  lineHeight="14px"
                  padding="0 12px"
                  text={translateLabel("btn_buy_credits", "Buy Credits")}
                />
              </div>
            </section>

            <div className="vendor-featured-plan-grid">
              {FEATURED_PLANS.map((plan) => (
                <article
                  className={`vendor-featured-plan-card ${
                    selectedPlan === plan.id ? "highlighted" : ""
                  }`}
                  key={plan.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedPlan(plan.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedPlan(plan.id);
                    }
                  }}
                >
                  <div className="vendor-featured-plan-mark">
                    <plan.Icon />
                  </div>
                  <div className="vendor-featured-plan-title">
                    {translateText(plan.name)}
                  </div>
                  <div className="vendor-featured-plan-description">
                    {translateText(plan.description)}
                  </div>
                  <div className="vendor-featured-price">
                    <div className="vendor-featured-price-value">
                      {plan.price}
                    </div>
                    <div className="vendor-featured-price-duration">
                      / {translateText(plan.duration)}
                    </div>
                  </div>
                  {plan.save && (
                    <div className="vendor-featured-save">{translateText(plan.save)}</div>
                  )}
                  <div className="vendor-featured-credits">
                    <div className="vendor-featured-credits-value">
                      {plan.credits}
                    </div>
                    <div className="vendor-featured-credits-label"> {translateLabel("txt_business_credits", "Credits")} </div>
                  </div>
                  <div className="vendor-featured-features">
                    {plan.features.map((feature) => (
                      <div key={feature}>{translateText(feature)}</div>
                    ))}
                  </div>
                  <GloablButton
                    className={`vendor-featured-plan-action ${
                      selectedPlan === plan.id ? "active" : ""
                    }`}
                    width="100%"
                    height="30px"
                    background={
                      selectedPlan === plan.id ? "#fa9c03" : "#ffffff"
                    }
                    border={
                      selectedPlan === plan.id
                        ? "1px solid #fa9c03"
                        : "1px solid #d0d5dd"
                    }
                    borderRadius="6px"
                    color={selectedPlan === plan.id ? "#ffffff" : "#344054"}
                    fontSize="10px"
                    lineHeight="14px"
                    padding="0 12px"
                    text={translateLabel("txt_web_vendor_purchase_plan", "Purchase Plan")}
                    onClick={() => setSelectedPlan(plan.id)}
                  />
                  {plan.badge && (
                    <div className="vendor-featured-plan-badge">
                      {translateText(plan.badge)}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorPromotions;
